import {hashCode} from '@packages/utilities/string-utils';
import {createLocationsMapSource} from './locationsMap';
import {createMultistepFormSource} from './multistepForm';
import {createPricingCalculatorSource} from './pricingCalculator';
import {createQuotesCarouselSource} from './quotesCarousel';
import {createShaderSource} from './shader';

export type {CodeComponentTemplateBranding} from './branding';

const CUSTOMIZE_WITH_AI_ASSISTANT =
  'Try it out for free and then customize with our AI Assistant';
const FOLLOW_UP_CONCLUSION =
  "You can answer any of these, skip the ones that don't apply, or just tell me to leave it as-is for now.";

export const CODE_COMPONENT_TEMPLATES = [
  {
    id: 'pricing-calculator',
    name: 'Pricing calculator',
    description:
      'A pricing section with monthly and yearly plans and a visitor selector.',
    createSource: createPricingCalculatorSource,
    picker: {
      heading: 'Scale further with a pricing calculator',
      body: 'Try it out for free and then customize pricing tiers and logic with our AI Assistant',
    },
    followUp: {
      introduction:
        "I've added a starter pricing calculator so you can see it in place. It's using placeholder plans and pricing right now.",
      heading:
        "Want me to match it to your actual pricing? A few things I'd need:",
      questions: [
        "Your plan names, tiers, and what's priced (per-seat, usage, flat, or a mix)",
        'Monthly vs. annual pricing, and any annual discount',
        'Any add-ons or overage costs to include',
        'Whether there\'s a "Contact us" / custom tier',
      ],
      conclusion: FOLLOW_UP_CONCLUSION,
    },
  },
  {
    id: 'shader',
    name: 'Shader',
    description:
      'An animated dithered wave background in your brand color, rendered with WebGL.',
    createSource: createShaderSource,
    picker: {
      heading: 'Make your heading pop with a shader',
      body: CUSTOMIZE_WITH_AI_ASSISTANT,
    },
    followUp: {
      introduction:
        "I've added a starter shader so you can see it in place. It's using your brand color and placeholder motion settings right now.",
      heading:
        'Want me to tailor it to your brand? A few things that would help:',
      questions: [
        'The colors you want in the waves',
        'Whether the motion should feel calmer or more energetic',
        'What should sit on top of it, like a heading or a call to action',
        'How tall the section should be',
      ],
      conclusion: FOLLOW_UP_CONCLUSION,
    },
  },
  {
    id: 'quotes-carousel',
    name: 'Quotes carousel',
    description:
      'A testimonial carousel with autoplay, arrow controls, and dot navigation.',
    createSource: createQuotesCarouselSource,
    picker: {
      heading: 'Build trust with a quote carousel',
      body: CUSTOMIZE_WITH_AI_ASSISTANT,
    },
    followUp: {
      introduction:
        "I've added a starter quotes carousel so you can see it in place. It's using placeholder testimonials right now.",
      heading:
        "Want me to fill it with your real testimonials? A few things I'd need:",
      questions: [
        "Each quote, with the person's name, role, and company",
        'Whether to show photos or company logos',
        'How fast it should rotate, or whether it should rotate at all',
      ],
      conclusion: FOLLOW_UP_CONCLUSION,
    },
  },
  {
    id: 'multistep-form',
    name: 'Multistep form',
    description:
      'A three-step signup form with a progress stepper, inline validation, and a success state.',
    createSource: createMultistepFormSource,
    picker: {
      heading: 'Learn more through a multistep form',
      body: CUSTOMIZE_WITH_AI_ASSISTANT,
    },
    followUp: {
      introduction:
        "I've added a starter multistep form so you can see it in place. It's using placeholder steps and fields right now.",
      heading:
        "Want me to match it to your signup flow? A few things I'd need:",
      questions: [
        'The steps you want and the fields in each one',
        'Which fields are required',
        'What should happen after someone submits, like a message or a redirect',
      ],
      conclusion: FOLLOW_UP_CONCLUSION,
    },
  },
  {
    id: 'locations-map',
    name: 'Locations map',
    description:
      'An interactive map with location pins and a list of addresses, hours, and phone numbers.',
    createSource: createLocationsMapSource,
    picker: {
      heading: 'Help people navigate with a map',
      body: CUSTOMIZE_WITH_AI_ASSISTANT,
    },
    followUp: {
      introduction:
        "I've added a starter locations map so you can see it in place. It's using placeholder locations right now.",
      heading: "Want me to add your actual locations? A few things I'd need:",
      questions: [
        'Your location names and addresses',
        'Any hours or phone numbers to show for each one',
        'Whether each location should link to directions',
      ],
      conclusion: FOLLOW_UP_CONCLUSION,
    },
  },
] as const;

export type CodeComponentTemplate = (typeof CODE_COMPONENT_TEMPLATES)[number];
export type CodeComponentTemplateId = CodeComponentTemplate['id'];

export const getCodeComponentTemplate = (id: string | undefined) =>
  CODE_COMPONENT_TEMPLATES.find((template) => template.id === id);

export function getUneditedCodeComponentTemplate(
  source:
    | {code: string; templateId?: string; templateSourceHash?: number}
    | null
    | undefined
) {
  const template = getCodeComponentTemplate(source?.templateId);
  return template &&
    source &&
    hashCode(source.code) === source.templateSourceHash
    ? template
    : undefined;
}
